import { Command } from 'commander'
import fs from 'node:fs'

import { findConfig } from '@/domains/project/helpers'
import { output } from '@/helpers/std-out'
import { parseAnything } from '@scalar-org/helpers/parse'
import { themePresets } from '@scalar-org/library/themes'
import {
  resolveSyncProject,
  type ScalarConfig,
  scalarConfigSchema,
} from '@scalar-org/scalar-config'
import { apiService, getAuthData, getDatabaseClient } from '@/helpers'
import { sleep } from '@/helpers/utils'
import { dirname, isAbsolute, join } from 'node:path'
import { publishRecordSchema } from '@scalar-org/entities/project'
import { defaultTheme } from '@scalar-org/entities/theme'

export const PublishCommand = () => {
  const cmd = new Command('publish')

  cmd.description(
    'Publish new build for a github sync project that is not linked.',
  )
  cmd.option('-s, --slug [slug]', 'project slug')
  cmd.option('-c, --config [config]', 'your config file of the project')
  cmd.action(async ({ config, slug }) => {
    const apiClient = await apiService()
    const dbClient = await getDatabaseClient()
    const auth = await getAuthData()
    auth.addEventListener((state) => Object.assign(auth, state))

    const file = config ?? findConfig()

    if (!fs.existsSync(file)) {
      return output
        .error()
        .title('Invalid configuration file')
        .message('Could not find the Scalar Configuration file:')
        .message(file)
        .exit('error')
    }

    const fileContent = fs.readFileSync(file, 'utf-8')
    const parsedConfigFile = parseAnything(fileContent)

    if (!parsedConfigFile.success) {
      return output
        .error()
        .title('Invalid configuration file')
        .line('Could not parse the Scalar Configuration file:')
        .message(file)
        .exit('error')
    }

    const { success, data: configFile } = scalarConfigSchema.safeParse(
      parsedConfigFile.data,
    )

    if (!success) {
      return output
        .error()
        .title('Invalid configuration file')
        .line(
          'Please check your scalar configuration file to match the specification.',
        )
        .message(file)
        .exit('error')
    }

    if (!slug) {
      return output
        .error()
        .title('Invalid argument')
        .message('Slug is required. Try passing --slug flag.')
        .exit('error')
    }

    const configTheme = await getTheme(configFile, apiClient)
    const gitProject = await getGithubProject(slug, apiClient)

    const configDir = dirname(file)

    const { project, documents, references, theme } = await resolveSyncProject<{
      path: string
    }>({
      config: configFile,
      theme: configTheme ?? undefined,
      gitProject,
      fileGetter: async ({ path }) => {
        const absolutePath = isAbsolute(path) ? path : join(configDir, path)
        return fs.readFileSync(absolutePath, 'utf-8')
      },
      folderGetter: async (_path: string) => {
        const path = isAbsolute(_path) ? _path : join(configDir, _path)
        const content = fs.readdirSync(path, { withFileTypes: true })

        return content
          .filter((e) => e.isFile() || e.isDirectory())
          .map((e) => {
            return {
              name: e.name,
              type: e.isFile() ? 'file' : 'dir',
              path: join(path, e.name),
            }
          })
      },
      parallel: true,
    })

    // Start the async flow
    const logger = output
      .info({ animate: true })
      .loader('Deployment in progress')

    const result = await apiClient.publish.deployCli({
      project,
      config: configFile,
      documents,
      references,
      theme,
    })

    if (result.error) {
      return output
        .error()
        .title('Something went wrong while deploying the project')
        .message(result.message)
        .exit('error')
    }

    // Check and update the deployment status since it is an asynchronous flow
    while (true) {
      const deployStatus = await getDeployStatus({
        client: dbClient,
        publishUid: result.data.publishUid,
        teamUid: auth.teamUid,
      })

      if (deployStatus.error === true) {
        return output
          .error()
          .title(
            'Something went wrong while getting information about the deployment',
          )
          .exit('error')
      }

      logger.update('loader', 0, { content: deployStatus.data.message })

      if (deployStatus.data.status === 'deployed') {
        return logger.exit('success')
      }

      if (deployStatus.data.status === 'error') {
        logger.exit('error')
      }

      // check the status every ~3 second
      await sleep(3000)
    }
  })

  return cmd
}

/**
 * Retrieves the theme configuration based on the provided config.
 *
 * Attempts to match the theme with built-in theme presets.
 * If no preset match is found, it fetches the theme from the remote API.
 */
async function getTheme(
  config: ScalarConfig,
  apiClient: Awaited<ReturnType<typeof apiService>>,
) {
  if (!config.siteConfig.theme) {
    return null
  }

  const slug = config.siteConfig.theme

  const presetTheme = themePresets.find((t) => t.slug === slug)

  if (presetTheme) {
    return presetTheme
  }

  // Fetch theme contents from slug
  const result = await apiClient.themes.get(slug)

  if (result.error) {
    output.warn().title('Something went wrong while loading theme').print()
    return null
  }

  return defaultTheme(result.data)
}

/**
 * Fetch github project metadata
 */
async function getGithubProject(
  slug: string,
  apiClient: Awaited<ReturnType<typeof apiService>>,
) {
  const project = await apiClient.github.getProject(slug)

  if (project.error) {
    return output
      .error()
      .title('Error fetching github project')
      .message(project.message)
      .exit('error')
  }

  return project.data
}

/** Returns the deploy entry on the database with all information on the current deployment */
async function getDeployStatus({
  client,
  publishUid,
  teamUid,
}: {
  client: Awaited<ReturnType<typeof getDatabaseClient>>
  publishUid: string
  teamUid: string
}) {
  return client.getOne({
    collectionPath: `team/${teamUid}/publish`,
    schema: publishRecordSchema,
    query: { uid: publishUid },
  })
}
